import { useState } from "react";
import { useT } from "@reciperoll/core";

const MULTIPLIERS = [0.25, 0.5, 1, 1.5, 2, 3];
const EPS = 1e-3;
// Free-scale slider bounds for "Custom" mode.
const CUSTOM_MIN = 0.25;
const CUSTOM_MAX = 10;
const CUSTOM_STEP = 0.1;

const roundScale = (v: number) => parseFloat(v.toFixed(2));

interface ScaleControlProps {
  baseServings: number | null;
  scale: number; // canonical: 1 = original
  onChange: (scale: number) => void;
}

// Unified recipe scaling (web mirror of the RN ScaleControl). Quick multiplier
// pills ("just double it"), plus — when the recipe has servings — a servings
// slider ("feed 6"), and a "Custom" mode toggle (in the header) for free scaling.
// All drive the same canonical `scale`.
//
// The Custom toggle has three states:
//  - inactive: a preset matches the current scale (that preset is highlighted).
//  - grey: the servings slider is on a non-preset value (a custom scale is in
//    effect) — tap to take manual control.
//  - orange: Custom (multiplier) mode is engaged. The slider becomes a free ×
//    slider and stays selected regardless of value.
//
// The parent modal unmounts when closed, so this remounts on each open — Custom
// mode resets with the recipe, initialised from the seeded scale below.
export function ScaleControl({ baseServings, scale, onChange }: ScaleControlProps) {
  const t = useT();
  const hasServings = !!baseServings && baseServings > 0;
  const matchesPreset = MULTIPLIERS.some((m) => Math.abs(scale - m) < EPS);
  // Whether the servings slider can faithfully show this scale: it snaps to whole
  // servings within [1, base×4], so a fractional or out-of-range target (e.g. 5.8×
  // of 5 = 29 servings, past the base×4 = 20 cap) can't be represented there and
  // would silently clamp.
  const servingsCanShow = (() => {
    if (!hasServings) return false;
    const target = scale * baseServings!;
    const hi = Math.max(baseServings! * 4, baseServings! + 1);
    return Number.isInteger(target) && target >= 1 && target <= hi;
  })();
  // Start in Custom (free-multiplier) mode when the scale is neither a preset nor
  // representable on the servings slider — e.g. resuming a cook at 5.8×, which would
  // otherwise clamp on the servings slider or be un-adjustable with no servings.
  const [customMode, setCustomMode] = useState(() => !matchesPreset && !servingsCanShow);

  const selectPreset = (m: number) => {
    setCustomMode(false);
    onChange(m);
  };
  const toggleCustom = () => setCustomMode((v) => !v);

  // Custom toggle styling — orange (mode on), grey (custom value in effect), else
  // a normal inactive pill.
  const customOrange = customMode;
  const customGrey = !customMode && !matchesPreset;

  const servingsHi = hasServings
    ? Math.max(baseServings! * 4, baseServings! + 1)
    : 1;
  const isOriginal = Math.abs(scale - 1) < EPS;

  return (
    <div>
      {/* Header: section label + the Custom mode toggle (uses the otherwise-empty
          space to the right, instead of orphaning Custom on its own pill row). */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-semibold text-ink-soft">{t("cook.scaling")}</p>
        <button
          onClick={toggleCustom}
          className={`px-3 py-1.5 rounded-full border text-sm font-semibold transition-colors ${
            customOrange
              ? "bg-terracotta border-terracotta text-white"
              : customGrey
                ? "bg-line border-line text-ink"
                : "bg-surface border-line text-ink-soft hover:bg-surface-2"
          }`}
        >
          {t("cook.custom")}
        </button>
      </div>

      <div className="flex flex-wrap justify-center gap-2">
        {MULTIPLIERS.map((m) => {
          // Presets never highlight while in Custom mode (Custom owns the selection).
          const active = !customMode && Math.abs(scale - m) < EPS;
          return (
            <button
              key={m}
              onClick={() => selectPreset(m)}
              className={`px-4 py-2 rounded-full border text-sm font-semibold transition-colors ${
                active
                  ? "bg-terracotta border-terracotta text-white"
                  : "bg-surface border-line text-ink-soft hover:bg-surface-2"
              }`}
            >
              {m === 1 ? t("cook.original") : `${m}×`}
            </button>
          );
        })}
      </div>

      {customMode ? (
        // Free multiplier slider — the value IS the scale.
        <div className="mt-5 rounded-2xl bg-surface-2 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-bold text-ink">{roundScale(scale)}×</span>
            {!isOriginal && (
              <button
                onClick={() => onChange(1)}
                className="text-sm font-semibold text-terracotta hover:text-terracotta-dark"
              >
                {t("cook.reset")}
              </button>
            )}
          </div>
          <input
            type="range"
            min={CUSTOM_MIN}
            max={CUSTOM_MAX}
            step={CUSTOM_STEP}
            value={scale}
            onChange={(e) => onChange(roundScale(parseFloat(e.target.value)))}
            className="w-full accent-terracotta"
          />
        </div>
      ) : hasServings ? (
        // Servings slider — maps servings ↔ scale via the base count.
        <div className="mt-5 rounded-2xl bg-surface-2 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-bold text-ink">
              {t("cook.serves", { count: Math.round(scale * baseServings!) })}
            </span>
            {!isOriginal && (
              <button
                onClick={() => onChange(1)}
                className="text-sm font-semibold text-terracotta hover:text-terracotta-dark"
              >
                {t("cook.reset")}
              </button>
            )}
          </div>
          <input
            type="range"
            min={1}
            max={servingsHi}
            step={1}
            value={Math.min(servingsHi, Math.round(scale * baseServings!))}
            onChange={(e) => onChange(parseInt(e.target.value, 10) / baseServings!)}
            className="w-full accent-terracotta"
          />
        </div>
      ) : null}
    </div>
  );
}

export default ScaleControl;
